import React, { useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";

import CardsHeader from "components/Headers/CardsHeader";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../hucs/util/helpers";
import { serverHost } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";

function Dashboard() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  // Change this if your route name is different
  const VISITOR_PASSES_ROUTE = "/admin/hrms/visitor-passes";

  // ---------------------------
  // STATE
  // ---------------------------
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [enterphonesSummary, setEnterphonesSummary] = React.useState({});
  const [directoriesSummary, setDirectoriesSummary] = React.useState({});
  const [tenantsSummary, setTenantsSummary] = React.useState({});
  const [proxy, setProxy] = React.useState(null);

  const [visitorPassesByDevice, setVisitorPassesByDevice] = React.useState([]);
  const [visitorPassesSummary, setVisitorPassesSummary] = React.useState({
    totalActive: 0,
    activeDevices: 0,
    expiringSoon: 0,
  });
  const [visitorPassesError, setVisitorPassesError] = React.useState(null);

  const token = getAuthToken();

  const selectedSiteRaw = localStorage.getItem("selectedSite");
  const selectedSite = selectedSiteRaw ? JSON.parse(selectedSiteRaw) : null;
  const siteUuid = selectedSite?.site_uuid;

  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;

  const hasGlobalRole = user?.role === "GLOBAL";

  const OFFLINE_AFTER_MS = 5 * 60 * 1000;
  const EXPIRING_SOON_MS = 2 * 60 * 60 * 1000;

  const [, setNowTick] = React.useState(0);

  const headerStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    color: isDark ? "#e2e8f0" : "#111",
  };

  const cardStyle = {
    backgroundColor: isDark ? "#111827" : undefined,
    color: isDark ? "#e2e8f0" : undefined,
  };

  const mutedTextStyle = {
    color: isDark ? "#9ca3af" : "#8898aa",
  };

  const readCachedProxyStatus = () => {
    try {
      const raw = localStorage.getItem("hrmsProxyStatus");
      if (!raw) return null;

      const data = JSON.parse(raw);

      if (data?.siteUuid && data.siteUuid !== siteUuid) return null;

      return data;
    } catch {
      return null;
    }
  };

  React.useEffect(() => {
    const id = setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => clearInterval(id);
  }, []);

  const getProxyStatus = (proxyData) => {
    const isEnabled = proxyData?.enable === "y";

    const lastCheckin = proxyData?.last_checkin
      ? new Date(proxyData.last_checkin)
      : null;

    const hasValidLastCheckin =
      lastCheckin && !Number.isNaN(lastCheckin.getTime());

    const isOnline =
      isEnabled && hasValidLastCheckin
        ? Date.now() - lastCheckin.getTime() < OFFLINE_AFTER_MS
        : false;

    if (!proxyData) {
      return {
        label: "Not Configured",
        color: "gray",
        lastCheckinText: "—",
      };
    }

    if (!isEnabled) {
      return {
        label: "Disabled",
        color: "gray",
        lastCheckinText: hasValidLastCheckin
          ? lastCheckin.toLocaleString()
          : "—",
      };
    }

    return {
      label: isOnline ? "Online" : "Offline",
      color: isOnline ? "green" : "red",
      lastCheckinText: hasValidLastCheckin
        ? lastCheckin.toLocaleString()
        : "—",
    };
  };

  const formatDashboardDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString("en-CA", {
      timeZone: "America/Vancouver",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;

    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) return false;

    const diff = expiry.getTime() - Date.now();

    return diff > 0 && diff <= EXPIRING_SOON_MS;
  };

  const getEarlierDate = (currentValue, nextValue) => {
    if (!currentValue) return nextValue;
    if (!nextValue) return currentValue;

    const currentDate = new Date(currentValue);
    const nextDate = new Date(nextValue);

    if (Number.isNaN(currentDate.getTime())) return nextValue;
    if (Number.isNaN(nextDate.getTime())) return currentValue;

    return nextDate < currentDate ? nextValue : currentValue;
  };

  const buildVisitorPassesByDevice = (devicesData = [], passesData = []) => {
    const deviceMap = new Map();

    // Start with every device so devices with 0 active passes still show.
    devicesData.forEach((device) => {
      if (!device?.device_uuid) return;

      deviceMap.set(String(device.device_uuid), {
        device_uuid: device.device_uuid,
        device_name: device.device_name || "Unnamed Device",
        device_ip: device.device_ip || "",
        active_count: 0,
        expiring_soon_count: 0,
        earliest_expires_at: null,
        visitors: [],
      });
    });

    passesData.forEach((pass) => {
      const deviceDetails = Array.isArray(pass.device_details)
        ? pass.device_details
        : [];

      deviceDetails.forEach((device) => {
        if (device?.status !== "active") return;

        const deviceUuid = device.device_uuid;
        if (!deviceUuid) return;

        const key = String(deviceUuid);

        if (!deviceMap.has(key)) {
          deviceMap.set(key, {
            device_uuid: deviceUuid,
            device_name: device.device_name || "Unnamed Device",
            device_ip: device.device_ip || "",
            active_count: 0,
            expiring_soon_count: 0,
            earliest_expires_at: null,
            visitors: [],
          });
        }

        const row = deviceMap.get(key);
        const expiresAt = device.expires_at || pass.expires_at;

        row.active_count += 1;
        row.earliest_expires_at = getEarlierDate(
          row.earliest_expires_at,
          expiresAt
        );

        if (isExpiringSoon(expiresAt)) {
          row.expiring_soon_count += 1;
        }

        row.visitors.push({
          visitor_pass_uuid: pass.visitor_pass_uuid,
          visitor_name: pass.visitor_name || "Unnamed Visitor",
          visitor_email: pass.visitor_email || "",
          expires_at: expiresAt,
        });
      });
    });

    return Array.from(deviceMap.values()).sort((a, b) => {
      if (b.active_count !== a.active_count) {
        return b.active_count - a.active_count;
      }

      return String(a.device_name || "").localeCompare(
        String(b.device_name || "")
      );
    });
  };

  const calculateVisitorPassesSummary = (rows) => {
    return {
      totalActive: rows.reduce((sum, row) => sum + row.active_count, 0),
      activeDevices: rows.filter((row) => row.active_count > 0).length,
      expiringSoon: rows.reduce(
        (sum, row) => sum + row.expiring_soon_count,
        0
      ),
    };
  };

  const loadDashboardData = async ({ isRefresh = false } = {}) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);
    setVisitorPassesError(null);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      if (!siteUuid) {
        setEnterphonesSummary({});
        setDirectoriesSummary({});
        setTenantsSummary({});
        setProxy(null);
        setVisitorPassesByDevice([]);
        setVisitorPassesSummary({
          totalActive: 0,
          activeDevices: 0,
          expiringSoon: 0,
        });
        return;
      }

      // HRMS Enterphones
      const enterphonesRes = await fetch(
        `${serverHost}/dashboard/hrms/enterphones_summary`,
        {
          headers: {
            ...headers,
            "x-site-uuid": siteUuid,
          },
        }
      );

      if (enterphonesRes.ok) {
        setEnterphonesSummary(await enterphonesRes.json());
      }

      // HRMS Directories
      const directoriesRes = await fetch(
        `${serverHost}/dashboard/hrms/directories_summary`,
        {
          headers: {
            ...headers,
            "x-site-uuid": siteUuid,
          },
        }
      );

      if (directoriesRes.ok) {
        setDirectoriesSummary(await directoriesRes.json());
      }

      // HRMS Tenants
      const tenantsRes = await fetch(
        `${serverHost}/dashboard/hrms/tenants_summary`,
        {
          headers: {
            ...headers,
            "x-site-uuid": siteUuid,
          },
        }
      );

      if (tenantsRes.ok) {
        setTenantsSummary(await tenantsRes.json());
      }

      // Proxy status
      const proxyRes = await fetch(
        `${serverHost}/hrms/proxy/by_site/${siteUuid}`,
        {
          headers: {
            ...headers,
          },
        }
      );

      if (proxyRes.ok) {
        const proxyRow = await proxyRes.json();
        setProxy(proxyRow);
      } else {
        setProxy(null);
      }

      // Visitor pass device summary
      const [visitorPassesRes, devicesRes] = await Promise.all([
        fetch(`${serverHost}/hrms/visitor_passes/${siteUuid}`, {
          headers: {
            ...headers,
          },
        }),
        fetch(`${serverHost}/hrms/get_devices/${siteUuid}`, {
          headers: {
            ...headers,
          },
        }),
      ]);

      let passesData = [];
      let devicesData = [];

      if (visitorPassesRes.ok) {
        passesData = await visitorPassesRes.json();
      } else {
        setVisitorPassesError("Visitor pass summary failed to load.");
      }

      if (devicesRes.ok) {
        devicesData = await devicesRes.json();
      } else {
        setVisitorPassesError("Device list failed to load.");
      }

      const groupedRows = buildVisitorPassesByDevice(devicesData, passesData);

      setVisitorPassesByDevice(groupedRows);
      setVisitorPassesSummary(calculateVisitorPassesSummary(groupedRows));
    } catch (err) {
      console.error(err);

      if (isRefresh) {
        setVisitorPassesError("Dashboard refresh failed.");
      } else {
        setError("Dashboard failed to load");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ---------------------------
  // PROXY STATUS LISTENER
  // ---------------------------
  React.useEffect(() => {
    const cached = readCachedProxyStatus();

    if (cached?.last_checkin) {
      setProxy((prev) => ({
        ...(prev || {}),
        last_checkin: cached.last_checkin,
      }));
    }

    const onProxyStatusUpdated = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      if (detail.siteUuid !== siteUuid) return;

      setProxy((prev) => ({
        ...(prev || {}),
        ...(detail.last_checkin ? { last_checkin: detail.last_checkin } : {}),
      }));
    };

    window.addEventListener("hrmsProxyStatusUpdated", onProxyStatusUpdated);

    return () =>
      window.removeEventListener("hrmsProxyStatusUpdated", onProxyStatusUpdated);
  }, [siteUuid]);

  // ---------------------------
  // LOAD DASHBOARD DATA
  // ---------------------------
  React.useEffect(() => {
    loadDashboardData();
  }, [token, siteUuid]);

  // ---------------------------
  // RENDER STATES
  // ---------------------------
  if (loading) {
    return (
      <Container fluid className="mt--6">
        <Card>
          <CardBody className="text-center">
            <Spinner /> Loading dashboard…
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="mt--6">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }

  const proxyStatus = getProxyStatus(proxy);

  const maxActiveCount = Math.max(
    ...visitorPassesByDevice.map((device) => device.active_count),
    1
  );

  const headerCards = [
    {
      title: "Enterphones",
      value: enterphonesSummary.total ?? 0,
      icon: "fas fa-tablet-alt",
      color: "primary",
      text: "Total enterphones",
      onClick: () => navigate("/admin/hrms/devices"),
    },
    {
      title: "Directories",
      value: directoriesSummary.total ?? 0,
      icon: "fas fa-address-book",
      color: "info",
      text: "Total directories",
      onClick: () => navigate("/admin/hrms/directories"),
    },
    {
      title: "Tenants",
      value: tenantsSummary.total ?? 0,
      icon: "fas fa-user",
      color: "orange",
      text: "Total tenants",
      onClick: () => navigate("/admin/hrms/tenants"),
    },
    {
      title: "Proxy",
      value: proxyStatus.label,
      icon: "fas fa-wifi",
      color: proxyStatus.color,
      text: `Last check-in: ${proxyStatus.lastCheckinText}`,
      onClick: hasGlobalRole
        ? () => navigate("/admin/hrms/proxy")
        : undefined,
    },
  ];

  // ---------------------------
  // DASHBOARD UI
  // ---------------------------
  return (
    <>
      <CardsHeader name="Dashboard" parentName="HRMS" cards={headerCards} />

      {/* <Container fluid className={`mt--6 ${isDark ? "table-dark-mode" : ""}`}>
        <Row>
          <Col xl="8" className="mb-4">
            <Card style={cardStyle}>
              <CardHeader
                className="border-0 header-theme"
                style={headerStyle}
              >
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0" style={{ color: headerStyle.color }}>
                      Visitor Passes
                    </h3>
                    <small style={mutedTextStyle}>
                      Shows active visitor access currently available on each device.
                    </small>
                  </Col>

                  <Col xs="4" className="text-right">
                    <Button
                      className="btn-round btn-icon"
                      color="info"
                      size="sm"
                      onClick={() => loadDashboardData({ isRefresh: true })}
                      disabled={refreshing}
                    >
                      <span className="btn-inner--icon mr-1">
                        <i className="fas fa-sync" />
                      </span>
                      <span className="btn-inner--text">
                        {refreshing ? "Refreshing..." : "Refresh"}
                      </span>
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {visitorPassesError && (
                  <Alert
                    color="warning"
                    toggle={() => setVisitorPassesError(null)}
                  >
                    {visitorPassesError}
                  </Alert>
                )}

                {visitorPassesByDevice.length === 0 ? (
                  <div className="text-center py-4" style={mutedTextStyle}>
                    No devices found for this site.
                  </div>
                ) : (
                  <div style={{ maxHeight: "430px", overflowY: "auto" }}>
                    {visitorPassesByDevice.map((device) => {
                      const barPercent =
                        device.active_count === 0
                          ? 0
                          : Math.max(
                              8,
                              Math.round(
                                (device.active_count / maxActiveCount) * 100
                              )
                            );

                      return (
                        <div
                          key={device.device_uuid}
                          className="mb-4 pb-3"
                          style={{
                            borderBottom: isDark
                              ? "1px solid #374151"
                              : "1px solid #e9ecef",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="font-weight-bold">
                                {device.device_name || "Unnamed Device"}
                              </div>

                              <small style={mutedTextStyle}>
                                {device.device_ip || "No IP address"}
                              </small>
                            </div>

                            <div className="text-right">
                              <span
                                className={
                                  device.active_count > 0
                                    ? "badge badge-success"
                                    : "badge badge-secondary"
                                }
                              >
                                {device.active_count} active
                              </span>

                              {device.expiring_soon_count > 0 && (
                                <span className="badge badge-warning ml-2">
                                  {device.expiring_soon_count} expiring soon
                                </span>
                              )}
                            </div>
                          </div>

                          <div
                            className="progress mt-3"
                            style={{
                              height: "8px",
                              backgroundColor: isDark ? "#374151" : "#e9ecef",
                            }}
                          >
                            <div
                              className={
                                device.active_count > 0
                                  ? "progress-bar bg-success"
                                  : "progress-bar bg-secondary"
                              }
                              role="progressbar"
                              style={{ width: `${barPercent}%` }}
                              aria-valuenow={device.active_count}
                              aria-valuemin="0"
                              aria-valuemax={maxActiveCount}
                            />
                          </div>

                          <div className="mt-2">
                            <small style={mutedTextStyle}>
                              Next expiry:{" "}
                              {formatDashboardDate(device.earliest_expires_at)}
                            </small>
                          </div>

                          {device.visitors.length > 0 ? (
                            <div className="mt-2">
                              {device.visitors.slice(0, 3).map((visitor) => (
                                <span
                                  key={`${device.device_uuid}-${visitor.visitor_pass_uuid}-${visitor.visitor_name}`}
                                  className="badge badge-light mr-2 mb-2"
                                  title={
                                    visitor.visitor_email
                                      ? `${visitor.visitor_email} · Expires ${formatDashboardDate(
                                          visitor.expires_at
                                        )}`
                                      : `Expires ${formatDashboardDate(
                                          visitor.expires_at
                                        )}`
                                  }
                                >
                                  {visitor.visitor_name}
                                </span>
                              ))}

                              {device.visitors.length > 3 && (
                                <span className="badge badge-secondary mr-2 mb-2">
                                  +{device.visitors.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="mt-2">
                              <small style={mutedTextStyle}>
                                No active visitor passes on this device.
                              </small>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" className="mb-4">
            <Card style={cardStyle}>
              <CardHeader
                className="border-0 header-theme"
                style={headerStyle}
              >
                <h3 className="mb-0" style={{ color: headerStyle.color }}>
                  Visitor Pass Summary
                </h3>
              </CardHeader>

              <CardBody>
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span style={mutedTextStyle}>Total active access</span>
                    <span className="h2 mb-0">
                      {visitorPassesSummary.totalActive}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span style={mutedTextStyle}>Devices with active access</span>
                    <span className="h2 mb-0">
                      {visitorPassesSummary.activeDevices}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span style={mutedTextStyle}>Expiring within 2 hours</span>
                    <span className="h2 mb-0">
                      {visitorPassesSummary.expiringSoon}
                    </span>
                  </div>
                </div>

                <Button
                  color="primary"
                  className="btn-round btn-icon"
                  onClick={() => navigate(VISITOR_PASSES_ROUTE)}
                >
                  <span className="btn-inner--icon mr-1">
                    <i className="fas fa-qrcode" />
                  </span>
                  <span className="btn-inner--text">Open Visitor Passes</span>
                </Button>

                <div className="mt-3">
                  <small style={mutedTextStyle}>
                    Active pass counts are based on device-level visitor pass
                    records. A pass for multiple devices will count once on each
                    device.
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container> */}
    </>
  );
}

export default Dashboard;